import styled, { css } from 'styled-components';
import { Typography } from '../typography';
import { linkConverter } from '../../utils';
import { colors } from '../../const';
import { Tooltip } from '../tooltip';

export const InputItemWrapper = styled.div`
  width: 100%;
  padding-top: var(--spacing-6);
  border-top: 0.1rem solid var(--gray-50);

  & > * {
    width: 100%;
  }

  & ~ * {
    border-top: none;
  }
`;

const InfoItemValue = styled(Typography)<{ breakSpaces?: boolean }>`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-self: right;

  width: max-content;
  grid-column-gap: var(--spacing-2);

  ${(props) =>
    props.breakSpaces &&
    css`
      display: initial;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      max-width: max-content;

      & > span {
        display: block;
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    `}
`;

const InfoItemBase = styled.div<{ withoutBorder?: boolean; size?: 'small' | 'medium' }>`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: var(--spacing-3);
  align-items: center;
  padding: var(--spacing-6) 0;

  &:nth-last-child(1) {
    padding-bottom: var(--spacing-3);
  }

  ${(props) =>
    !props.withoutBorder &&
    css`
      border-top: 0.1rem solid var(--gray-50);
    `}

  ${(props) =>
    props.size === 'small' &&
    css`
      padding: var(--spacing-2) 0;
    `}
`;

interface InfoItemProps {
  className?: string;
  label: any;
  value: any;
  size?: 'small' | 'medium';
  startAdornment?: any;
  withoutBorder?: boolean;
  capitalized?: boolean;
  color?: keyof typeof colors;
  breakSpaces?: boolean;
}

export const InfoItem: React.FC<InfoItemProps> = ({
  withoutBorder,
  label,
  value,
  startAdornment,
  capitalized,
  color = '--gray-80',
  breakSpaces,
  ...props
}: InfoItemProps) => {
  if (typeof value === 'object' && !!value?.length) {
    value = value
      .filter((val: string) => !!val)
      .join(' ')
      .trim();
  }
  if (!value) {
    startAdornment = '';
    value = 'N / A';
  }

  return (
    <InfoItemBase withoutBorder={withoutBorder} {...props}>
      <Typography variant={'textM'} weight={'medium'} color={'--gray-90'}>
        {label}
      </Typography>
      <InfoItemValue
        variant={'textS'}
        color={color}
        align={'right'}
        capitalized={capitalized}
        breakSpaces={breakSpaces}
      >
        {startAdornment}
        {linkConverter(value)}
      </InfoItemValue>
    </InfoItemBase>
  );
};

export const InfoItemTooltip: React.FC<InfoItemProps> = ({
  withoutBorder,
  label,
  value,
  startAdornment,
  capitalized,
  color = '--gray-80',
  breakSpaces,
  ...props
}: InfoItemProps) => {
  if (!value) {
    startAdornment = '';
    value = 'N / A';
  }

  return (
    <InfoItemBase withoutBorder={withoutBorder} {...props}>
      <Typography variant={'textM'} weight={'medium'} color={'--gray-90'}>
        {label}
      </Typography>
      <Tooltip title={value} placement={'top'} arrow>
        <InfoItemValue
          variant={'textS'}
          color={color}
          align={'right'}
          capitalized={capitalized}
          breakSpaces={breakSpaces}
        >
          {startAdornment}
          {value}
        </InfoItemValue>
      </Tooltip>
    </InfoItemBase>
  );
};
